import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Label,
  useToast,
} from '@ais-ui/ui-core';
import { useState } from 'react';
import { ShipList } from '@ais-ui/api-spec/config';
import { useDeleteShiplist } from '@ais-ui/api';
import { useLocalization } from '@ais-ui/locale-management';

interface DeleteShiplistProps {
  shiplist: ShipList;
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

export function DeleteShiplist({
  shiplist,
  showDialog,
  setShowDialog,
}: DeleteShiplistProps) {
  const { messages } = useLocalization();
  const { mutate } = useDeleteShiplist();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const onDelete = (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    const shiplistName = shiplist.name;

    if (!shiplist.id) {
      toast({
        className: 'delete-shiplist-error-toast',
        title: messages.filtersDeleteShiplistToastErrorTitle,
        description: `${messages.filtersDeleteShiplistToastErrorDescription} ${shiplistName}`,
        variant: 'destructive',
      });
      setShowDialog(false);
      setIsSubmitting(false);
      return;
    }

    mutate(shiplist.id, {
      onSuccess: () => {
        toast({
          className: 'delete-shiplist-success-toast',
          title: messages.filtersDeleteShiplistToastSuccessTitle,
          description: messages.filtersDeleteShiplistToastSuccessDescription,
        });

        setShowDialog(false);
        setIsSubmitting(false);
      },
      onError: (error: any) => {
        if (error.response?.status === 403) {
          toast({
            className: 'delete-shiplist-error-toast',
            title: messages.filtersDeleteShiplistToastForbiddenTitle,
            description: `${messages.filtersDeleteShiplistToastForbiddenDescription} ${shiplistName}`,
            variant: 'destructive',
          });
        } else {
          toast({
            className: 'delete-shiplist-error-toast',
            title: messages.filtersDeleteShiplistToastErrorTitle,
            description: `${messages.filtersDeleteShiplistToastFailedDescription} ${shiplistName}.`,
            variant: 'destructive',
          });
        }
        setShowDialog(false);
        setIsSubmitting(false);
      },
    });
  };

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogTrigger asChild>
        <Button
          id="shiplist-delete-button"
          variant="ghost"
          className="text-primary hover:text-primary"
        >
          Delete
        </Button>
      </DialogTrigger>
      <DialogContent id="mutate-shiplist-dialog">
        <DialogHeader>
          <DialogTitle>{messages.shiplistsDeleteTitle}</DialogTitle>
          <DialogDescription>
            {messages.shiplistsDeleteFilterWarning}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onDelete}>
          <div className="grid grid-cols-[auto_minmax(50%,_1fr)] 4 gap-x-2 items-baseline">
            <Label className="font-light">
              {messages.shiplistsDeleteShiplistName}
            </Label>
            <Label htmlFor="shiplist-name">{shiplist.name}</Label>
          </div>
          <DialogFooter className="p-4">
            <Button
              disabled={isSubmitting}
              variant="destructive"
              id="shiplist-confirm-delete-button"
            >
              {isSubmitting
                ? messages.genericSubmitting
                : messages.genericSubmit}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
